import { Select } from '@ardoq/select';
import { FlexBox, Stack } from '@ardoq/layout';
import { CronSchedulingPolicy, SchedulingPolicy } from '@ardoq/api-types';
import {
  cronPolicyToScheduleState,
  formatUtcHourToLocalTime,
  migrateDeprecatedSchedulingPolicy,
  scheduleStateToCronExpression,
} from './utils';
import {
  HOUR_OPTIONS,
  HourOptionValue,
  ScheduleType,
  SCHEDULE_TYPE_OPTIONS,
  WEEK_DAY_OPTIONS,
  WeekOptionValues,
} from './types';
import { getScheduleTypeLabel, getWeekDayLabel } from './labels';
import styled from 'styled-components';
import { Paragraph } from '@ardoq/typography';
import { type I18nLocale, t } from '@ardoq/i18n';

const WeekAndTimeContainer = styled(FlexBox).attrs({
  gap: 'medium',
  justify: 'space-between',
})`
  > div {
    flex: 1;
  }
`;

type SchedulePolicySelectorProps = {
  title: string;
  schedulePolicy: SchedulingPolicy;
  onSchedulePolicyChange: (schedulingPolicy: SchedulingPolicy) => void;
  i18nLocale: I18nLocale;
};

export const SchedulePolicySelector = ({
  title,
  schedulePolicy,
  onSchedulePolicyChange,
  i18nLocale,
}: SchedulePolicySelectorProps) => {
  const migratedPolicy = migrateDeprecatedSchedulingPolicy(schedulePolicy);
  const scheduleState = cronPolicyToScheduleState(migratedPolicy);

  const handleIntervalChange = (value: ScheduleType | null) => {
    if (!value) {
      return;
    }
    if (value === 'nightly') {
      const newPolicy: CronSchedulingPolicy = {
        type: 'cron',
        cronExpression: scheduleStateToCronExpression({
          scheduleType: 'nightly',
          hour: scheduleState.hour,
        }),
      };
      onSchedulePolicyChange(newPolicy);
      return;
    }
    const newMigratedPolicy: CronSchedulingPolicy = {
      type: 'cron',
      cronExpression: scheduleStateToCronExpression({
        scheduleType: 'weekly',
        weekDay:
          scheduleState.scheduleType === 'weekly'
            ? scheduleState.weekDay
            : WEEK_DAY_OPTIONS[0].value,
        hour: scheduleState.hour,
      }),
    };

    onSchedulePolicyChange(newMigratedPolicy);
  };

  const handleWeekDayChange = (value: WeekOptionValues | null) => {
    if (!value || scheduleState.scheduleType === 'nightly') {
      return;
    }

    onSchedulePolicyChange({
      type: 'cron',
      cronExpression: scheduleStateToCronExpression({
        scheduleType: 'weekly',
        weekDay: value,
        hour: scheduleState.hour,
      }),
    });
  };

  const handleHourChange = (value: HourOptionValue | null) => {
    if (!value) {
      return;
    }

    if (scheduleState.scheduleType === 'nightly') {
      onSchedulePolicyChange({
        type: 'cron',
        cronExpression: scheduleStateToCronExpression({
          scheduleType: 'nightly',
          hour: value,
        }),
      });
    } else {
      onSchedulePolicyChange({
        type: 'cron',
        cronExpression: scheduleStateToCronExpression({
          scheduleType: 'weekly',
          weekDay: scheduleState.weekDay,
          hour: value,
        }),
      });
    }
  };

  const scheduleTypeOptions = SCHEDULE_TYPE_OPTIONS.map(option => ({
    label: getScheduleTypeLabel(i18nLocale, option.value),
    value: option.value,
  }));

  const weekDayOptions = WEEK_DAY_OPTIONS.map(option => ({
    label: getWeekDayLabel(i18nLocale, option.value),
    value: option.value,
  }));

  return (
    <Stack gap="small">
      <Select
        label={title}
        value={scheduleState.scheduleType}
        options={scheduleTypeOptions}
        onValueChange={value => handleIntervalChange(value)}
        i18nLocale={i18nLocale}
      />
      <WeekAndTimeContainer justify="space-between" gap="medium">
        {scheduleState.scheduleType === 'weekly' && (
          <Select
            label={t(
              i18nLocale,
              'integrations:schedulePolicySelector.runEveryLabel',
              'Run every'
            )}
            value={scheduleState.weekDay}
            options={weekDayOptions}
            onValueChange={value => handleWeekDayChange(value)}
            i18nLocale={i18nLocale}
          />
        )}
        <Select
          label={t(
            i18nLocale,
            'integrations:schedulePolicySelector.scheduledTimeLabel',
            'Scheduled time (UTC)'
          )}
          value={scheduleState.hour}
          options={HOUR_OPTIONS.map(o => ({
            label: o.label,
            description: t(
              i18nLocale,
              'integrations:schedulePolicySelector.localTimeDescription',
              { localTime: formatUtcHourToLocalTime(o.value) },
              '{{localTime}} your local time'
            ),
            value: o.value,
          }))}
          onValueChange={value => handleHourChange(value)}
          i18nLocale={i18nLocale}
        />
      </WeekAndTimeContainer>
      <Paragraph variant="caption" color="textSubtle">
        {t(
          i18nLocale,
          'integrations:schedulePolicySelector.utcExplanation',
          'Time is in UTC. Oslo working hours (08:00-18:00 CET/CEST) are excluded.'
        )}
      </Paragraph>
    </Stack>
  );
};
